import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Input,
  Space,
  Button,
  Tooltip,
  Tag,
  message,
} from "antd";
import {
  EyeOutlined,
  FileDoneOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import Header166 from "../header/header";
import AsignacionSostenibilidad from "./AsignacionSostenibilidad";

import { getPlanesCompraSostenibilidad } from "../../services/financiero/plandecompras/planesdecomprasostenibilidad";

const money = (v) =>
  (v ?? 0).toLocaleString("es-CO", { style: "currency", currency: "COP" });

export default function EntregasSostenibilidad() {
  const [planes, setPlanes] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Modal hijo
  const [openModal, setOpenModal] = useState(false);
  const [planEdit, setPlanEdit] = useState(null);

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPlan, setPreviewPlan] = useState(null);

  const username = useSelector((s) => s?.auth?.user?.username || "");

  // Para deshabilitar códigos ya usados
  const [existingPlanCodes, setExistingPlanCodes] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Carga de planes
  const fetchPlanes = async () => {
    try {
      setLoading(true);
      const r = await getPlanesCompraSostenibilidad();
      if (r?.status === 200) {
        const parsed = r.data.map((item) => ({
          ...item,
          dataItems:
            typeof item.dataItems === "string"
              ? JSON.parse(item.dataItems)
              : item.dataItems,
          conceptoInversion:
            typeof item.conceptoInversion === "string"
              ? JSON.parse(item.conceptoInversion)
              : item.conceptoInversion,
        }));
        setPlanes(parsed);
        setFiltered(parsed);
        setExistingPlanCodes(
          Array.from(new Set(parsed.map((p) => p.codigoInterno))).filter(
            Boolean
          )
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanes();
  }, []);

  // Buscar
  const onSearch = (e) => {
    const v = (e.target.value || "").toLowerCase();
    setSearchText(v);
    if (!v) {
      setFiltered(planes);
      return;
    }
    setFiltered(
      planes.filter((p) => {
        if (p.codigoInterno?.toLowerCase().includes(v)) return true;
        if (Array.isArray(p.dataJson)) {
          return p.dataJson.some(
            (x) =>
              x.codPre?.toLowerCase().includes(v) ||
              x.implementador?.toLowerCase().includes(v)
          );
        }
        return false;
      })
    );
  };

  // Acciones
  const handleEdit = (rec) => {
    setPlanEdit(rec);
    setOpenModal(true);
  };
  const handlePreview = (rec) => {
    setPreviewPlan(rec);
    setPreviewOpen(true);
  };

  const columns = [
    {
      title: "Acciones",
      key: "acciones",
      align: "center",
      render: (_, r) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(r)}
          />
          <Button
            type="primary"
            style={{ background: "#28a745" }}
            icon={<FileDoneOutlined />}
            onClick={() => handleEdit(r)}
          />
        </Space>
      ),
    },
    {
      title: "Código PRE",
      dataIndex: "dataJson",
      key: "codPre",
      align: "center",
      width: 150,
      render: (arr) =>
        Array.isArray(arr)
          ? arr
              .map((i) => i.codPre)
              .filter(Boolean)
              .join(", ") || "-"
          : "-",
    },
    {
      title: "Implementador",
      dataIndex: "dataJson",
      key: "implementador",
      align: "center",
      width: 200,
      render: (arr) =>
        Array.isArray(arr)
          ? arr
              .map((i) => i.implementador)
              .filter(Boolean)
              .join(", ") || "-"
          : "-",
    },
    {
      title: "Código Interno",
      dataIndex: "codigoInterno",
      key: "codigoInterno",
      align: "center",
      width: 150,
      render: (t) => (
        <Tooltip title={t}>
          <span>
            {(t || "").length > 10 ? `${t.slice(0, 10)}...` : t || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Total Proyecto",
      dataIndex: "conceptoInversion",
      key: "total",
      align: "right",
      width: 150,

      render: (ci) =>
        money(ci?.find((c) => c.concepto === "TOTAL PROYECTO")?.valor),
    },
    {
      title: "A. Infraestructura",
      dataIndex: "conceptoInversion",
      key: "infra",
      align: "right",
      width: 150,

      render: (ci) =>
        money(ci?.find((c) => c.concepto === "A. Infraestructura")?.valor),
    },
    {
      title: "B. Activos Productivos y equipos",
      dataIndex: "conceptoInversion",
      key: "act",
      align: "right",
      width: 200,

      render: (ci) =>
        money(
          ci?.find((c) => c.concepto === "B. Activos Productivos y equipos")
            ?.valor
        ),
    },
    {
      title: "C. Herramientas y Mobiliario",
      dataIndex: "conceptoInversion",
      key: "herr",
      align: "right",
      width: 150,

      render: (ci) =>
        money(
          ci?.find((c) => c.concepto === "C. Herramientas y Mobiliario")?.valor
        ),
    },
    {
      title: "D. Insumos",
      dataIndex: "conceptoInversion",
      key: "ins",
      align: "right",
      width: 150,

      render: (ci) =>
        money(ci?.find((c) => c.concepto === "D. Insumos")?.valor),
    },
    {
      title: "E. Gastos Operativos",
      dataIndex: "conceptoInversion",
      key: "gas",
      align: "right",
      width: 150,

      render: (ci) =>
        money(ci?.find((c) => c.concepto === "E. Gastos Operativos")?.valor),
    },
    {
      title: "F. Contrapartida",
      dataIndex: "conceptoInversion",
      key: "contra",
      align: "right",
      width: 150,

      render: (ci) =>
        money(ci?.find((c) => c.concepto === "F. Contrapartida")?.valor),
    },
    {
      title: "Fecha Creación",
      dataIndex: "fecha_creacion",
      key: "fc",
      align: "center",
      width: 150,

      render: (f) =>
        new Date(f).toLocaleDateString("es-CO", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
    },
    {
      title: "Última Actualización",
      dataIndex: "fecha_actualizacion",
      key: "fa",
      align: "center",
      width: 150,

      render: (f) =>
        new Date(f).toLocaleDateString("es-CO", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
    },
  ];

  return (
    <div className="container mt-4">
      <Header166 type="entregasSostenibilidad" />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Buscar por Código o Implementador"
          value={searchText}
          onChange={onSearch}
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 250 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filtered}
        loading={loading}
        rowKey="oid"
        bordered
        scroll={{ y: 500, x: "max-content" }}
        pagination={{
          current: currentPage, // página actual
          pageSize: pageSize, // filas por página
          total: filtered.length, // total de registros
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        size="small"
      />

      {/* Modal hijo */}
      <AsignacionSostenibilidad
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setPlanEdit(null);
        }}
        planId={planEdit?.oid ?? null} // ✅ si es null, pasa null                existingPlanCodes={existingPlanCodes}
      />

      {/* Preview */}
      <Modal
        title={
          <span style={{ fontWeight: "bold" }}>
            📄 Detalles del Plan de Compras
          </span>
        }
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width="50%"
        style={{ top: 20 }}
        footer={
          <Button type="primary" onClick={() => setPreviewOpen(false)}>
            Cerrar
          </Button>
        }
      >
        {previewPlan && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Tag color="green">Código Interno:</Tag>{" "}
              {previewPlan.codigoInterno}
            </div>
            <div>
              <Tag color="gold">Fecha de Creación:</Tag>{" "}
              {new Date(previewPlan.fecha_creacion).toLocaleString("es-CO")}
            </div>
            <div>
              <Tag color="purple">Última Actualización:</Tag>{" "}
              {new Date(previewPlan.fecha_actualizacion).toLocaleString(
                "es-CO"
              )}
            </div>

            <div>
              <h4>Conceptos de Inversión</h4>
              <Table
                dataSource={previewPlan.conceptoInversion}
                pagination={false}
                rowKey="concepto"
                columns={[
                  { title: "Concepto", dataIndex: "concepto" },
                  {
                    title: "Valor",
                    dataIndex: "valor",
                    align: "right",
                    render: (v) => money(v),
                  },
                  {
                    title: "% del Total",
                    dataIndex: "porcentaje",
                    align: "center",
                  },
                ]}
                size="small"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
